import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Trophy, Medal, Crown, Star, Flame, Zap, Target, Award,
  ChevronRight, Clock, TrendingUp, Users, Sparkles, 
  ArrowLeft, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LeaderboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [timeframe, setTimeframe] = useState('all');
  const [myStats, setMyStats] = useState(null);
  const [totalParticipants, setTotalParticipants] = useState(0);
  
  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    fetchLeaderboard();
    if (token) {
      fetchMyStats();
    }
  }, [timeframe, token]);

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/api/codearena/leaderboard?timeframe=${timeframe}&limit=50`);
      setLeaderboard(res.data.leaderboard || []);
      setTotalParticipants(res.data.total_participants || 0);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyStats = async () => {
    try {
      const res = await axios.get(`${API}/api/codearena/me/xp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyStats(res.data);
    } catch (err) {
      console.error('Failed to fetch my stats:', err);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <Crown className="text-yellow-500" size={24} />;
      case 2: return <Medal className="text-gray-400" size={24} />;
      case 3: return <Medal className="text-amber-600" size={24} />;
      default: return <span className="text-gray-500 font-bold w-6 text-center">{rank}</span>;
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1: return 'bg-gradient-to-r from-yellow-500/20 to-yellow-500/5 border-yellow-500/30';
      case 2: return 'bg-gradient-to-r from-gray-400/20 to-gray-400/5 border-gray-400/30';
      case 3: return 'bg-gradient-to-r from-amber-600/20 to-amber-600/5 border-amber-600/30';
      default: return 'bg-white/5 border-white/10';
    }
  };

  const getLevelColor = (level) => {
    if (level >= 10) return 'text-purple-400';
    if (level >= 5) return 'text-cyan-400';
    if (level >= 2) return 'text-green-400';
    return 'text-gray-400';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <header className="bg-[#12121a] border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link 
                to="/codearena/dashboard" 
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-400" />
              </Link>
              <div>
                <h1 className="text-lg font-bold text-white flex items-center gap-2">
                  <Trophy className="text-yellow-500" />
                  Global Leaderboard
                </h1>
                <p className="text-xs text-gray-500">{totalParticipants.toLocaleString()} participants</p>
              </div>
            </div>
            
            {/* Timeframe Filter */}
            <div className="flex bg-white/5 rounded-lg p-1">
              {['all', 'monthly', 'weekly'].map((tf) => (
                <button
                  key={tf}
                  onClick={() => setTimeframe(tf)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    timeframe === tf 
                      ? 'bg-purple-600 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {tf === 'all' ? 'All Time' : tf.charAt(0).toUpperCase() + tf.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* My Stats Card */}
        {myStats && (
          <div className="mb-8 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">#{myStats.rank}</span>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Your Rank</p>
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl font-bold ${getLevelColor(myStats.level)}`}>
                      Level {myStats.level}
                    </span>
                    <span className="text-gray-500">•</span>
                    <span className="text-white font-semibold">{myStats.total_xp.toLocaleString()} XP</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="flex items-center gap-1 text-orange-400">
                    <Flame size={18} />
                    <span className="text-xl font-bold">{myStats.current_streak}</span>
                  </div>
                  <p className="text-xs text-gray-500">Day Streak</p>
                </div>
                
                <div className="text-center">
                  <div className="flex items-center gap-1 text-cyan-400">
                    <Award size={18} />
                    <span className="text-xl font-bold">{myStats.badge_count}</span>
                  </div>
                  <p className="text-xs text-gray-500">Badges</p>
                </div>
                
                <div className="text-center">
                  <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full transition-all"
                      style={{ width: `${(myStats.xp_in_level / 1000) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{myStats.xp_to_next_level} XP to next level</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-purple-500" size={40} />
          </div>
        ) : (
          <div className="space-y-3">
            {/* Top 3 Cards */}
            {leaderboard.slice(0, 3).length > 0 && (
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {/* Second Place */}
                {leaderboard[1] && (
                  <div className="md:order-1 bg-gradient-to-b from-gray-400/20 to-transparent border border-gray-400/30 rounded-2xl p-6 text-center md:mt-8">
                    <div className="w-16 h-16 mx-auto bg-gray-500 rounded-full flex items-center justify-center mb-3">
                      <Medal className="text-white" size={32} />
                    </div>
                    <p className="text-xl font-bold text-white">{leaderboard[1].name}</p>
                    <p className="text-gray-500 text-sm mb-2">{leaderboard[1].college || 'Unknown'}</p>
                    <div className="flex items-center justify-center gap-2">
                      <Sparkles className="text-gray-400" size={16} />
                      <span className="text-gray-300 font-semibold">{leaderboard[1].total_xp?.toLocaleString()} XP</span>
                    </div>
                    <p className={`text-sm mt-1 ${getLevelColor(leaderboard[1].level)}`}>
                      Level {leaderboard[1].level}
                    </p>
                  </div>
                )}
                
                {/* First Place */}
                {leaderboard[0] && (
                  <div className="md:order-2 bg-gradient-to-b from-yellow-500/30 to-transparent border border-yellow-500/30 rounded-2xl p-6 text-center">
                    <Crown className="text-yellow-500 mx-auto mb-2" size={32} />
                    <div className="w-20 h-20 mx-auto bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full flex items-center justify-center mb-3">
                      <Trophy className="text-white" size={40} />
                    </div>
                    <p className="text-2xl font-bold text-white">{leaderboard[0].name}</p>
                    <p className="text-gray-500 text-sm mb-2">{leaderboard[0].college || 'Unknown'}</p>
                    <div className="flex items-center justify-center gap-2">
                      <Sparkles className="text-yellow-400" size={16} />
                      <span className="text-yellow-300 font-bold text-lg">{leaderboard[0].total_xp?.toLocaleString()} XP</span>
                    </div>
                    <p className={`text-sm mt-1 ${getLevelColor(leaderboard[0].level)}`}>
                      Level {leaderboard[0].level}
                    </p>
                  </div>
                )}
                
                {/* Third Place */}
                {leaderboard[2] && (
                  <div className="md:order-3 bg-gradient-to-b from-amber-600/20 to-transparent border border-amber-600/30 rounded-2xl p-6 text-center md:mt-12">
                    <div className="w-14 h-14 mx-auto bg-amber-600 rounded-full flex items-center justify-center mb-3">
                      <Medal className="text-white" size={28} />
                    </div>
                    <p className="text-lg font-bold text-white">{leaderboard[2].name}</p>
                    <p className="text-gray-500 text-sm mb-2">{leaderboard[2].college || 'Unknown'}</p>
                    <div className="flex items-center justify-center gap-2">
                      <Sparkles className="text-amber-400" size={16} />
                      <span className="text-amber-300 font-semibold">{leaderboard[2].total_xp?.toLocaleString()} XP</span>
                    </div>
                    <p className={`text-sm mt-1 ${getLevelColor(leaderboard[2].level)}`}>
                      Level {leaderboard[2].level}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Rest of Leaderboard */}
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              {leaderboard.slice(3).map((entry) => (
                <div 
                  key={entry.id}
                  className={`flex items-center justify-between p-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center">
                      {getRankIcon(entry.rank)}
                    </div>
                    <div>
                      <p className="font-medium text-white">{entry.name}</p>
                      <p className="text-sm text-gray-500">{entry.college || 'Unknown'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-white font-semibold">{entry.total_xp?.toLocaleString()} XP</p>
                      <p className={`text-xs ${getLevelColor(entry.level)}`}>Level {entry.level}</p>
                    </div>
                    
                    {entry.badge_count > 0 && (
                      <div className="flex items-center gap-1 text-cyan-400">
                        <Award size={16} />
                        <span className="text-sm">{entry.badge_count}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {leaderboard.length === 0 && (
                <div className="p-12 text-center text-gray-500">
                  <Users className="mx-auto mb-3 opacity-50" size={48} />
                  <p>No participants yet</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LeaderboardPage;
